export type MessageType = 'standardMessage' | 'bidMessage' | 'intentionMessage' | 'quotationMessage';

export type ConversationMessage = {
  _id: string;
  adminId: { name: string; email: string; avatarUrl?: string } | null;
  supplierMemberId: { name: string; email: string } | null;
  overrideInformation?: { senderName?: string; senderEmail?: string };
  message: string;
  attachment: Array<{ file: string; fileName: string; mimeType?: string }>;
  createdAt: string;
  isUnread: boolean;
};

// What every inbox row needs, whichever side of the conversation is reading it.
export type InboxRowItem = {
  conversationId: string;
  title: string;       // area name
  preview: string;
  senderName: string;
  senderInitials: string;
  messageType: MessageType;
  projectId: string;
  projectName: string;
  areaId: string;
  supplierId: string;
  supplierMemberId: string;
  latestMsgId: string;
  unreadCount: number;   // unread message count; derive read state as unreadCount === 0
  groupConvCount: number;  // total conversations in this row's group (all pages)
  groupUnreadTotal: number; // total unread messages in the group (all pages)
  updatedAt: string;
};

// A purchaser reading supplier threads in their own projects.
export type InboxItem = InboxRowItem & {
  supplierName: string;
  projectSlug: string;
  areaSlug: string;
  supplierSlug: string;
  supplierMemberSlug: string;
};

// A supplier contact reading buyer threads across buyer organisations.
export type SalesInboxItem = InboxRowItem & {
  instance?: string;   // host of the single-tenant instance holding the thread; absent when it is this one
  buyerOrgId: string;
  buyerOrgName: string;
  buyerOrgSlug: string;
  projectSlug: string;
  areaSlug: string;
};

export type InboxAttachment = ConversationMessage['attachment'][number];

export type InboxFilter = 'all' | 'unread';

export type InboxFetchOptions = {
  filter: InboxFilter;
  messageTypes: MessageType[];
  projectIds: string[];
  q: string;
  cursor?: string;
};

export type InboxPage<Item> = { items: Item[]; hasMore: boolean; nextCursor?: string };

export const INBOX_PAGE_SIZE = 25;

export const toInboxQuery = (opts: InboxFetchOptions) => ({
  filter: opts.filter === 'unread' ? 'unread' as const : undefined,
  messageTypes: opts.messageTypes.length > 0 ? opts.messageTypes : undefined,
  projectIds: opts.projectIds,
  q: opts.q || undefined,
  cursor: opts.cursor,
  limit: INBOX_PAGE_SIZE,
});

export const cursorOf = (item: Pick<InboxRowItem, 'updatedAt' | 'conversationId'>) =>
  `${new Date(item.updatedAt).toISOString()}|${item.conversationId}`;

export type InboxAdapter<Item extends InboxRowItem> = {
  fetchPage: (opts: InboxFetchOptions) => Promise<InboxPage<Item>>;
  fetchGroupPage: (lastItem: Item, opts: InboxFetchOptions) => Promise<InboxPage<Item>>;
  messageTypes: MessageType[];   // offered in the type filter
  createActivity: (item: Item) => boolean;   // offer "Skapa aktivitet" on this row and in its panel
  groupKey: (item: Item) => string;
  counterpart: (item: Item) => string;   // the other party's name on a row
  markRead: (item: Item) => Promise<unknown>;
  markUnread: (item: Item) => Promise<unknown>;
  refreshBadge: () => void;
  markAllRead: () => Promise<unknown>;
  loadThread: (item: Item) => Promise<ConversationMessage[]>;   // chronological
  sendReply: (item: Item, text: string, files: File[]) => Promise<unknown>;
  isOwnMessage: (msg: ConversationMessage, item: Item) => boolean;
  downloadAttachment: (item: Item, attachment: InboxAttachment) => Promise<unknown>;
  conversationLink?: (item: Item) => string | undefined;   // panel footer; absent or falsy hides it
  sideLabel?: (item: Item) => { labelKey: string; fallback: string };
};
